/**
 * <s-hero-video> — Muted, looping background video with a visible pause/play control.
 *
 * Plays only when the visitor allows motion; a reduced-motion preference leaves the poster frame.
 * An explicit pause is sticky until the visitor presses play again.
 *
 * Commands: --toggle (from the pause/play button).
 * Required children: video, button[data-toggle] with data-label-pause / data-label-play.
 */
class SHeroVideo extends HTMLElement {
  #ac;
  #video;
  #button;
  #userPaused = false;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    this.#video = this.querySelector('video');
    this.#button = this.querySelector('[data-toggle]');
    if (!this.#video) return;

    this.#video.muted = true;
    this.#video.playsInline = true;

    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    motion.addEventListener('change', () => this.#sync(motion.matches), { signal });
    document.addEventListener('visibilitychange', () => this.#sync(motion.matches), { signal });
    this.addEventListener('command', (event) => {
      if (event.command !== '--toggle') return;
      this.#userPaused = !this.#video.paused;
      this.#sync(motion.matches);
    }, { signal });

    this.#sync(motion.matches);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #sync(reducedMotion) {
    const shouldPlay = !this.#userPaused && !reducedMotion && !document.hidden;
    if (shouldPlay) {
      this.#video.play().catch(() => this.#render());
    } else {
      this.#video.pause();
    }
    this.#render(shouldPlay);
  }

  #render(playing = !this.#video.paused) {
    if (!this.#button) return;
    const label = playing ? this.#button.dataset.labelPause : this.#button.dataset.labelPlay;
    this.#button.setAttribute('aria-label', label);
    this.#button.setAttribute('aria-pressed', playing ? 'false' : 'true');
    this.#button.dataset.state = playing ? 'playing' : 'paused';
  }
}

customElements.define('s-hero-video', SHeroVideo);
